/* eslint-disable no-await-in-loop */
/* eslint-disable no-console */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-undef */
import React, { useEffect, useState, useRef } from 'react';
import {
  StyleSheet, View, Text, ScrollView, Button, Platform, Alert, Share, Linking,
} from 'react-native';
import { useSelector } from 'react-redux';
import * as FileSystem from 'expo-file-system';
import ViewShot from 'react-native-view-shot';
import * as pdfLib from 'pdf-lib';
import { StandardFonts } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import LineChartWidget from '../components/Occupancy/LineChartWidget';
import PieChartWidget from '../components/Occupancy/PieChartWidget';
import TimesDashboard from '../components/Occupancy/TimesDashboard';
import WeatherDashboard from '../components/Occupancy/WeatherDashboard';
import ProgressBarWidget from '../components/Occupancy/ProgressBarWidget';
import Colors from '../constants/Colors';
import { DateRange } from '../redux/dateRangeSlice';
import SpaceUtilizationWidget from '../components/Occupancy/LocationOccupancyDashboard/components/SpaceUtilizationWidget';
import OccupancyIcon16 from '../components/16pxIcons/OccupancyIcon16';
import LineGraph from '../graphs/LineGraph';
import GetMetric, { AggregationFunction } from '../requests/Sensors/GetMetric';
import { SampleOccupancyData } from '../components/Occupancy/SampleData';
import BarGraph from '../graphs/BarGraph';
import { Metric } from '../constants/Types';
import { screenMaxWidth } from '../components/TopSection';
import { getImageBytes } from './Analytics/Utils';

export default function WidgetShowcaseScreen() {
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => (state.dateRange));
  const compareRange: DateRange = useSelector((state: { dateComparisonRange: DateRange }) => (state
    .dateComparisonRange));
  const [occupancyData, setOccupancyData] = useState([]);
  const [occupancyDataForDay, setOccupancyDataForDay] = useState([]);
  const viewShotRef = useRef<ViewShot>(null);
  const lineChartRef = useRef<ViewShot>(null);
  const pieChartRef = useRef<ViewShot>(null);
  const spaceUtilRef = useRef<ViewShot>(null);
  const barGraphRef = useRef<ViewShot>(null);
  const timesDashboardRef = useRef<ViewShot>(null);
  const weatherDashboardRef = useRef<ViewShot>(null);
  const progressBarRef = useRef<ViewShot>(null);
  const progressBarDetailRef = useRef<ViewShot>(null);
  const circularProgressRef = useRef<ViewShot>(null);

  const captureWidget = async (ref) => {
    if (!ref.current) return null;
    try {
      // Use different capture options based on platform
      const captureOptions = Platform.OS === 'web'
        ? { format: 'png', quality: 0.9, result: 'base64' }
        : { format: 'png', quality: 0.9 };

      const uri = await ref.current.capture(captureOptions);

      // For web platform, convert base64 data to data URL
      if (Platform.OS === 'web') {
        return `data:image/png;base64,${uri}`;
      }
      return uri;
    } catch (error) {
      console.error("Couldn't capture view:", error);
      return null;
    }
  };

  const exportToPDF = async () => {
    // Capture all widgets
    const images = [];
    const lineChartImage = await captureWidget(lineChartRef);
    if (lineChartImage) images.push({ name: 'Line Chart', uri: lineChartImage });

    const pieChartImage = await captureWidget(pieChartRef);
    if (pieChartImage) images.push({ name: 'Pie Chart', uri: pieChartImage });

    const spaceUtilImage = await captureWidget(spaceUtilRef);
    if (spaceUtilImage) images.push({ name: 'Space Utilization', uri: spaceUtilImage });

    const barGraphImage = await captureWidget(barGraphRef);
    if (barGraphImage) images.push({ name: 'Bar Graph', uri: barGraphImage });

    const timesDashboardImage = await captureWidget(timesDashboardRef);
    if (timesDashboardImage) images.push({ name: 'Times Dashboard', uri: timesDashboardImage });

    const weatherDashboardImage = await captureWidget(weatherDashboardRef);
    if (weatherDashboardImage) images.push({ name: 'Weather Dashboard', uri: weatherDashboardImage });

    const progressBarImage = await captureWidget(progressBarRef);
    if (progressBarImage) images.push({ name: 'Progress Bar', uri: progressBarImage });

    const progressBarDetailImage = await captureWidget(progressBarDetailRef);
    if (progressBarDetailImage) images.push({ name: 'Progress Bar Detail', uri: progressBarDetailImage });

    const circularProgressImage = await captureWidget(circularProgressRef);
    if (circularProgressImage) images.push({ name: 'Circular Progress', uri: circularProgressImage });

    if (images.length === 0) {
      Alert.alert('Error', 'Failed to capture any widgets.');
      return;
    }

    imagesToPdf(images);
  };

  // Function to export widgets to PDF using pdf-lib
  const imagesToPdf = async (images) => {
    try {
      Alert.alert('Capturing Widgets', 'Please wait while we capture all widgets on screen...');

      // Create PDF document
      const pdfDoc = await pdfLib.PDFDocument.create();

      // Add fonts
      pdfDoc.registerFontkit(fontkit);
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

      // Get metrics for report
      const summaryValue = occupancyData.length > 0 ? Math.max(...occupancyData.map((item) => item.value || 0)).toFixed(0) : '120';
      const avgUtil = occupancyData.length > 0 ? Math.round(occupancyData.reduce((sum, item) => sum + (item.value || 0), 0) / occupancyData.length) : '75';

      // Create title page
      const titlePage = pdfDoc.addPage([595.28, 841.89]); // A4 size
      const { width, height } = titlePage.getSize();

      // Add title and date
      titlePage.drawText('Widget Showcase Report', {
        x: 50,
        y: height - 100,
        size: 24,
        font: helveticaFont,
        color: pdfLib.rgb(0.1, 0.1, 0.1),
      });

      titlePage.drawLine({
        start: { x: 50, y: height - 120 },
        end: { x: 200, y: height - 120 },
        thickness: 2,
        color: pdfLib.rgb(0.2, 0.4, 0.8),
      });

      titlePage.drawText(`Generated: ${new Date().toLocaleString()}`, {
        x: 50,
        y: height - 150,
        size: 12,
        font: helveticaFont,
        color: pdfLib.rgb(0.4, 0.4, 0.4),
      });

      // Add summary section
      titlePage.drawRectangle({
        x: 50,
        y: height - 250,
        width: width - 100,
        height: 80,
        color: pdfLib.rgb(0.95, 0.95, 0.98),
        borderColor: pdfLib.rgb(0.2, 0.4, 0.8),
        borderWidth: 1,
        opacity: 0.8,
      });

      titlePage.drawText('Summary', {
        x: 60,
        y: height - 220,
        size: 16,
        font: helveticaFont,
        color: pdfLib.rgb(0.2, 0.2, 0.2),
      });

      titlePage.drawText(`Max Occupancy: ${summaryValue} people`, {
        x: 60,
        y: height - 245,
        size: 12,
        font: helveticaFont,
      });

      titlePage.drawText(`Average Utilization: ${avgUtil}%`, {
        x: 250,
        y: height - 245,
        size: 12,
        font: helveticaFont,
      });

      titlePage.drawText(`Captured Widgets: ${images.length}`, {
        x: 430,
        y: height - 245,
        size: 12,
        font: helveticaFont,
      });

      // Add Hereworks branding
      titlePage.drawText('Hereworks Admin Dashboard', {
        x: width / 2 - 100,
        y: 40,
        size: 14,
        font: helveticaFont,
        color: pdfLib.rgb(0.2, 0.4, 0.8),
      });

      // Add each widget to the PDF
      let currentY = height - 280;
      const imageWidth = width - 100;
      const imageHeight = 200;

      // Process each captured image
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < images.length; i++) {
        // Add a new page if we're out of space
        if (currentY < imageHeight + 60) {
          const newPage = pdfDoc.addPage([595.28, 841.89]);
          currentY = newPage.getSize().height - 100;

          // Add page header
          newPage.drawText('Widget Showcase Report', {
            x: 50,
            y: newPage.getSize().height - 50,
            size: 14,
            font: helveticaFont,
            color: pdfLib.rgb(0.2, 0.4, 0.8),
          });

          newPage.drawLine({
            start: { x: 50, y: newPage.getSize().height - 60 },
            end: { x: newPage.getSize().width - 50, y: newPage.getSize().height - 60 },
            thickness: 1,
            color: pdfLib.rgb(0.8, 0.8, 0.8),
          });
        }

        const currentPage = pdfDoc.getPages()[pdfDoc.getPageCount() - 1];

        try {
          // Get image bytes
          const imageBytes = await getImageBytes(images[i].uri);

          // Embed image in PDF
          let pdfImage;
          if (images[i].uri.toLowerCase().endsWith('.jpg') || images[i].uri.toLowerCase().endsWith('.jpeg')) {
            pdfImage = await pdfDoc.embedJpg(imageBytes as Uint8Array);
          } else {
            pdfImage = await pdfDoc.embedPng(imageBytes as Uint8Array);
          }

          // Calculate image dimensions to maintain aspect ratio
          const imgDims = pdfImage.scale(0.5); // Scale down to fit page
          const scaleFactor = Math.min(imageWidth / imgDims.width, imageHeight / imgDims.height);
          const scaledWidth = imgDims.width * scaleFactor;
          const scaledHeight = imgDims.height * scaleFactor;

          // Draw widget name
          currentPage.drawText(images[i].name, {
            x: 50,
            y: currentY,
            size: 14,
            font: helveticaFont,
            color: pdfLib.rgb(0.2, 0.2, 0.2),
          });

          // Draw image
          currentPage.drawImage(pdfImage, {
            x: 50,
            y: currentY - scaledHeight - 10,
            width: scaledWidth,
            height: scaledHeight,
          });

          // Update position for next image
          currentY -= (scaledHeight + 50); // Height + padding
        } catch (error) {
          console.error(`Error processing image ${images[i].name}:`, error);
          currentPage.drawText(`Error loading ${images[i].name}: ${error.message}`, {
            x: 50,
            y: currentY - 20,
            size: 10,
            font: helveticaFont,
            color: pdfLib.rgb(0.8, 0.2, 0.2),
          });
          currentY -= 40;
        }
      }

      // Save PDF to a file
      const pdfBytes = await pdfDoc.save();
      const timestamp = new Date().getTime();

      // Handle different platforms
      if (Platform.OS === 'web') {
        // For web, create a blob and download it
        const blob = new Blob([pdfBytes] as any, { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `WidgetShowcase_${timestamp}.pdf`;
        link.click();

        // Clean up
        URL.revokeObjectURL(url);
        Alert.alert('Success', 'PDF has been downloaded.');
      } else {
        // For mobile, save to file system and share
        const pdfUri = `${FileSystem.documentDirectory}WidgetShowcase_${timestamp}.pdf`;
        const base64Data = Buffer.from(pdfBytes).toString('base64');
        await FileSystem.writeAsStringAsync(pdfUri, base64Data, {
          encoding: FileSystem.EncodingType.Base64,
        });

        Alert.alert(
          'PDF Generated',
          'The PDF has been created. Would you like to share it?',
          [
            {
              text: 'Cancel',
              style: 'cancel',
            },
            {
              text: 'Share',
              onPress: async () => {
                await Share.share({
                  url: pdfUri,
                  title: 'Widget Showcase Report',
                });
              },
            },
            {
              text: 'View',
              onPress: async () => {
                await Linking.openURL(pdfUri);
              },
            },
          ],
        );
      }
    } catch (error) {
      console.error('PDF export error:', error);
      Alert.alert('Error', `Failed to create PDF: ${error.message}`);
    }
  };

  useEffect(() => {
    GetMetric({
      metric: Metric.OCCUPANCY,
      range,
      aggregationFunction: AggregationFunction.MAX,
    }).then((res) => {
      setOccupancyDataForDay(
        res?.filter((item: { timestamp: string, value: number | null }) => new Date(item.timestamp)
          .getDay() === 0 + 1)
          ?.map((item: { timestamp: string, value: number | null }, index: number) => ({
            index, date: item.timestamp, value: item.value,
          })),
      );
    }).catch(() => setOccupancyData(SampleOccupancyData));
  }, [range]);

  useEffect(() => {
    GetMetric({
      metric: Metric.OCCUPANCY,
      range,
      aggregationFunction: AggregationFunction.MAX,
    }).then((res) => {
      setOccupancyData(
        res?.map((item: { timestamp: string, value: number | null }, index: number) => ({
          index, date: item.timestamp, value: item.value,
        })),
      );
    }).catch(() => setOccupancyDataForDay(SampleOccupancyData));
  }, [range]);

  return (
    <ViewShot ref={viewShotRef} options={{ format: 'png', quality: 0.9, result: Platform.OS === 'web' ? 'base64' : 'tmpfile' }} style={styles.container}>
      <View style={styles.topRightButtonContainer}>
        <View style={styles.buttonRow}>
          <Button title="Export Widgets" onPress={exportToPDF} />
        </View>
      </View>
      <ScrollView contentContainerStyle={styles.content}>
        <Text style={{ marginTop: 30 }}>Widget examples:</Text>

        <ViewShot ref={lineChartRef} options={{ format: 'png', quality: 0.9, result: Platform.OS === 'web' ? 'base64' : 'tmpfile' }}>
          <LineChartWidget
            locationName={'Line Chart'}
            range={range} compareRange={compareRange} />
        </ViewShot>

        <ViewShot ref={pieChartRef} options={{ format: 'png', quality: 0.9, result: Platform.OS === 'web' ? 'base64' : 'tmpfile' }}>
          <View style={[styles.graphCard, { flex: 1, minWidth: 260 }]}>
            <PieChartWidget
              locationName={'Pie Chart'}
              range={range} compareRange={compareRange} />
          </View>
        </ViewShot>

        <ViewShot ref={spaceUtilRef} options={{ format: 'png', quality: 0.9, result: Platform.OS === 'web' ? 'base64' : 'tmpfile' }}>
          <SpaceUtilizationWidget
            label='Average occupancy'
            count={120}
            unit='ppl'
            icon={<OccupancyIcon16 />}
            title='Occupancy'
            content={<LineGraph
              style={[styles.lineGraph]}
              title='Occupancy'
              noTitle
              alternateTitle='Queue'
              chartData={occupancyData}
            />}
          />
        </ViewShot>

        <ViewShot ref={barGraphRef} options={{ format: 'png', quality: 0.9, result: Platform.OS === 'web' ? 'base64' : 'tmpfile' }}>
          <SpaceUtilizationWidget
            icon={<OccupancyIcon16 />}
            title='Occupancy'
            content={<BarGraph
              style={{}}
              title='Occupancy'
              chartData={occupancyDataForDay}
            />}
          />
        </ViewShot>

        <ViewShot ref={timesDashboardRef} options={{ format: 'png', quality: 0.9, result: Platform.OS === 'web' ? 'base64' : 'tmpfile' }}>
          <TimesDashboard />
        </ViewShot>

        <ViewShot ref={weatherDashboardRef} options={{ format: 'png', quality: 0.9, result: Platform.OS === 'web' ? 'base64' : 'tmpfile' }}>
          <WeatherDashboard />
        </ViewShot>

        <ViewShot ref={progressBarRef} options={{ format: 'png', quality: 0.9, result: Platform.OS === 'web' ? 'base64' : 'tmpfile' }}>
          <ProgressBarWidget
            data={[
              { value: 50, title: 'Title1' },
              { value: 50, title: 'Title2' },
              { value: 50, title: 'Title3' },
              { value: 50, title: 'Title4' },
              { value: 50, title: 'Title2' },
              { value: 50, title: 'Title3' },
              { value: 50, title: 'Title4' },
              { value: 50, title: 'Title3' },
              { value: 50, title: 'Title4' },
              { value: 50, title: 'Title1' },
              { value: 50, title: 'Title2' },
              { value: 50, title: 'Title3' },
              { value: 50, title: 'Title4' },
              { value: 50, title: 'Title2' },
              { value: 50, title: 'Title3' },
              { value: 50, title: 'Title4' },
              { value: 50, title: 'Title3' },
              { value: 50, title: 'Title4' },
            ]}
          />
        </ViewShot>

        <ViewShot ref={progressBarDetailRef} options={{ format: 'png', quality: 0.9, result: Platform.OS === 'web' ? 'base64' : 'tmpfile' }}>
          <ProgressBarWidget
            data={[
              { value: 5, title: 'Title 1', count: 10 },
              { value: 10, title: 'Title 2', count: 20 },
              { value: 15, title: 'Title 3', count: 30 },
              { value: 20, title: 'Title 4', count: 40 },
              { value: 25, title: 'Title 5', count: 50 },
              { value: 30, title: 'Title 6', count: 60 },
              { value: 35, title: 'Title 7', count: 70 },
              { value: 40, title: 'Title 8', count: 80 },
              { value: 45, title: 'Title 9', count: 90 },
            ]}
            withDetail
          />
        </ViewShot>

        <ViewShot ref={circularProgressRef} options={{ format: 'png', quality: 0.9, result: Platform.OS === 'web' ? 'base64' : 'tmpfile' }}>
          <ProgressBarWidget
            data={[
              { value: 50, title: 'Title 1' },
              { value: 30, title: 'Title 2' },
              { value: 40, title: 'Title 3' },
              { value: 90, title: 'Title 4' },
              { value: 10, title: 'Title 5' },
              { value: 20, title: 'Title 6' },
              { value: 40, title: 'Title 7' },
            ]}
            circular
          />
        </ViewShot>

      </ScrollView>
    </ViewShot>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.light.hereworksBackground,
    flex: 1,
  },
  content: {
    padding: 40,
    maxWidth: screenMaxWidth,
  },
  graphCard: {
    borderRadius: 16,
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite300,
    marginVertical: 12,
  },
  grid: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  lineGraph: {
    marginTop: -90,
    marginStart: -36,
    marginBottom: -40,
  },
  topRightButtonContainer: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 1,
  },
  buttonRow: {
    flexDirection: 'row',
  },
  buttonSpacer: {
    width: 10,
  },
});
